/* eslint-disable css-modules/no-unused-class */
import classNames from 'classnames';
import HTMLDOMParser from 'html-dom-parser';
import { domToReact } from 'html-react-parser';
import { forwardRef, memo } from 'react';
import Image from '@/components/atoms/image/image';
import type { TypographyStyle } from '@/components/atoms/typography/typography';
import styles from './rich-text.module.scss';

export interface RichTextProps {
  /** The class name to apply */
  className?: string;
  /** Additional styles to apply */
  style?: React.CSSProperties;
  /** The rich text to show */
  text: string;
  /** The typography variant to apply to the component */
  typographyVariant?: TypographyStyle;
}

const BLOCK_LEVEL_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'div',
  'dl',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'ul',
]);

const DISALLOWED_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'meta',
  'link',
  'base',
]);

const isUnsafeUrl = (value: string) => {
  const normalized = value.trim().toLowerCase();

  return (
    normalized.startsWith('javascript:') || normalized.startsWith('vbscript:')
  );
};

/**
 * Keeps only safe HTML attributes.
 * - Removes inline event handlers (for example `onclick`).
 * - Removes inline styles and `srcdoc`.
 * - Removes `href`/`src` values using unsafe protocols.
 */
const sanitizeAttributes = (attribs?: Record<string, string>) => {
  if (!attribs) return {};

  return Object.entries(attribs).reduce<Record<string, string>>(
    (safeAttributes, [key, value]) => {
      const lowerCaseKey = key.toLowerCase();

      if (lowerCaseKey.startsWith('on')) return safeAttributes;

      if (lowerCaseKey === 'style' || lowerCaseKey === 'srcdoc') {
        return safeAttributes;
      }

      if (
        (lowerCaseKey === 'href' || lowerCaseKey === 'src') &&
        isUnsafeUrl(value)
      ) {
        return safeAttributes;
      }

      safeAttributes[key] = value;

      return safeAttributes;
    },
    {},
  );
};

/**
 * Sanitizes parser nodes and normalizes invalid nesting.
 * - Removes disallowed tags and unsafe attributes,
 * - Rewrites `<p>` nodes that contain block-level elements to `<div>`
 * to avoid React DOM nesting warnings.
 */
const sanitizeAndNormalizeNodes = (nodes: any[], parent?: any): any[] => {
  return nodes
    .map(node => {
      if (node.name && DISALLOWED_TAGS.has(node.name.toLowerCase()))
        return null;

      if (node.type !== 'tag') return node;

      const sanitizedNode = {
        ...node,
        attribs: sanitizeAttributes(node.attribs),
      };

      if (Array.isArray(sanitizedNode.children)) {
        sanitizedNode.children = sanitizeAndNormalizeNodes(
          sanitizedNode.children,
          sanitizedNode,
        );
      }

      if (
        sanitizedNode.name === 'p' &&
        Array.isArray(sanitizedNode.children) &&
        sanitizedNode.children.some(
          child => child?.type === 'tag' && BLOCK_LEVEL_TAGS.has(child.name),
        )
      ) {
        sanitizedNode.name = 'div';
      }

      sanitizedNode.parent = parent ?? null;

      return sanitizedNode;
    })
    .filter(Boolean);
};

/**
 * Parses rich HTML text after sanitization and converts `img` tags
 * to the shared `Image` atom.
 */
const parseAndReplaceImages = (value: string) => {
  const parsedNodes = HTMLDOMParser(value);
  const sanitizedNodes = sanitizeAndNormalizeNodes(parsedNodes);

  const parsedContent = domToReact(sanitizedNodes, {
    replace: (node, nodeIndex) => {
      if (node?.type === 'tag' && node?.name === 'img' && node?.attribs?.src) {
        const { src, alt, width, height } = node.attribs;

        const parsedHeight = Number.parseInt(height, 10);
        const parsedWidth = Number.parseInt(width, 10);

        return (
          <Image
            key={nodeIndex}
            alt={alt ?? ''}
            height={Number.isNaN(parsedHeight) ? 100 : parsedHeight}
            src={src}
            unoptimized
            width={Number.isNaN(parsedWidth) ? 100 : parsedWidth}
          />
        );
      }
    },
  });

  return parsedContent;
};

const RichText = forwardRef<HTMLDivElement, RichTextProps>(
  (
    { className, style, typographyVariant = 'bodyMedium', text, ...rest },
    ref,
  ) => {
    const parsedContent = parseAndReplaceImages(text);

    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.component, className, {
          [styles[typographyVariant]]: typographyVariant,
        })}
        style={style}
      >
        {parsedContent}
      </div>
    );
  },
);

RichText.displayName = 'RichText';

export default memo(RichText);
